import React, { useEffect, useState } from 'react';
import t from 'react-translate';
import { css } from '@emotion/core';
import { useDispatch, useSelector } from 'react-redux';

import NvModal, { BackdropType, ModalTheme, ModalType } from 'shared/components/nv-modal';

import OfferingCard from 'offerings/components/offering-card';
import CardsResponsiveListing from 'dashboard/components/cards-responsive-listing';

import { CombinedCourse } from 'redux/schemas';
import { getCurrentCourse } from 'redux/selectors/course';
import { getCurrentUserJourneys } from 'redux/selectors/learning-journeys';
import { getCurrentEnrollment, getCurrentUserEnrollments } from 'redux/selectors/users';

import { standardSpacing } from 'styles/global_defaults/scaffolding';
import { normalFontWeight, textLargeFontSize } from 'styles/global_defaults/fonts';
import { fetchCurrentUserEnrollments } from 'redux/actions/users';

const modalStyles = css`
  &.bs4-modal {
    &.modal-light {
      .bs4-modal-dialog {
        .bs4-modal-content {
          .bs4-modal-header {
            padding: ${standardSpacing}px 0;
            height: auto;

            .bs4-modal-title {
              height: auto;
            }

            button {
              padding: 0;
              margin: 0;
              top: ${standardSpacing}px;
              right: ${standardSpacing}px;
            }
          }

          .bs4-modal-body {
            margin: 0;
          }
        }
      }
    }
  }
`;

const bodyStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  .message {
    margin-bottom: ${standardSpacing}px;
    font-size: ${textLargeFontSize}px;
    font-weight: ${normalFontWeight};
  }

  .cards-container {
    width: 100%;
  }
`;

function JourneyProgressModal() {
  const { catalogId, inJourneys } = useSelector(getCurrentCourse);
  const journeys = useSelector(getCurrentUserJourneys);
  const enrollment = useSelector(getCurrentEnrollment);
  const enrollments = useSelector(getCurrentUserEnrollments);

  const [show, setShow] = useState(false);
  const onClose = () => setShow(false);

  const dispatch = useDispatch();

  const currentCourseJourneysIds = inJourneys?.map(({ id }) => id);
  const currentCourseJourneys = journeys.filter(({ id, released }) => currentCourseJourneysIds?.includes(id) && released);
  const currentCourseJourneysEnrollments = currentCourseJourneysIds
    ?.map(id => enrollments.find(({ courseId }) => id === courseId))
    ?.filter(Boolean);

  const modalWidth = getModalWidth(currentCourseJourneys.length);

  const isCourseComplete = enrollment?.completionStatus;
  const isJourneysCompleted = currentCourseJourneysEnrollments?.every(({ completionStatus }) => Boolean(completionStatus))
    || currentCourseJourneys?.every(({ completionStats }) => completionStats.collections.every(({ completed, required }) => completed >= required));

  const displayOneStepCloser = currentCourseJourneys.length === 1 && !isJourneysCompleted;
  const displayCompletedJourney = currentCourseJourneys.length === 1 && isJourneysCompleted;

  useEffect(() => {
    const visitedCourseKey = enrollment && `${catalogId}-${enrollment.userId}`;
    const visitedCourseHomeValue = localStorage.getItem('visitedCourses');
    const visitedCoursesHome = JSON.parse(visitedCourseHomeValue);
    const isCurrentCourseVisited = Boolean(visitedCourseKey && visitedCoursesHome && visitedCoursesHome[visitedCourseKey]);

    if (currentCourseJourneys.length && isCourseComplete && !isCurrentCourseVisited) {
      const newVisitedCoursesHomeValue = {
        ...(visitedCoursesHome || {}),
        [visitedCourseKey]: true,
      };

      localStorage.setItem('visitedCourses', JSON.stringify(newVisitedCoursesHomeValue));
      setShow(true);
    }
  }, [catalogId, enrollment, currentCourseJourneys, isCourseComplete]);

  useEffect(() => {
    dispatch(fetchCurrentUserEnrollments());
  }, [dispatch]);

  return (
    <NvModal
      show={show}
      onClose={onClose}
      width={modalWidth}
      type={ModalType.FIXED}
      theme={ModalTheme.LIGHT}
      modalStyles={modalStyles}
      header={t.LEARNING_JOURNEYS.COURSE_HOME.MODAL.CONGRATULATIONS()}
      backdropType={BackdropType.LIGHT}
      body={(
        <div css={bodyStyles}>
          <span className='message'>
            {displayOneStepCloser && t.LEARNING_JOURNEYS.COURSE_HOME.MODAL.YOU_ARE_ONE_STEP_CLOSER()}
            {displayCompletedJourney && t.LEARNING_JOURNEYS.COURSE_HOME.MODAL.YOU_HAVE_COMPLETED_THE_JOURNEY()}
            {currentCourseJourneys.length > 1 && t.LEARNING_JOURNEYS.COURSE_HOME.MODAL.CONTINUE_WITH_JOURNEYS()}
          </span>
          <div className='cards-container'>
            <CardsResponsiveListing
              items={currentCourseJourneys}
              keyExtractor={(journey: CombinedCourse) => journey.id}
              renderItem={({ item, props: cardProps }) => (
                <OfferingCard
                  offering={item}
                  ref={cardProps.ref}
                  style={cardProps.style}
                  useJourneyInferedCompletion
                />
              )}
            />
          </div>
        </div>
      )}
    />
  );
}

const getModalWidth = (numOfCards: number): number => {
  if (numOfCards === 1) return 440;
  if (numOfCards === 2) return 680;

  return 1000;
};

export default JourneyProgressModal;
